import useVaults from '../vaults/useVaults';
import useTokens from '../tokens/useTokens';
import type { LiquidityPool, Vault } from 'nftx.js';
import type { TokenInterface } from '@/core/tokens';
import { Address } from 'viem';
import { useQuery } from '@tanstack/react-query';
import { QueryKey } from '../../queryKeys';

export const useVaultCollections = () => {
  const { data: vaults } = useVaults();
  const { data: tokens } = useTokens();

  return useQuery(
    [QueryKey.VAULT_COLLECTIONS, vaults, tokens],
    () => {
      if (!tokens?.length || !vaults?.length) {
        return [];
      }

      const data: Record<
        `0x${string}`,
        {
          collectionAddress: Address;
          slug: string;
          vaults: Vault[];
          tokens: TokenInterface[];
          pools: LiquidityPool[];
        }
      > = {};

      vaults.forEach((cur) => {
        const token = tokens.find((x) => x.id.toLowerCase() === cur.id);
        if (!token) {
          return;
        }
        const pools = token?.pools || [];

        let collection = data[cur.asset.id];

        if (!collection) {
          collection = data[cur.asset.id] = {
            collectionAddress: cur.asset.id,
            slug: cur.collectionSlug,
            vaults: [],
            tokens: [],
            pools: [],
          };
        }

        collection.vaults.push(cur);
        collection.tokens.push(token);
        collection.pools.push(...pools);
      });

      return Object.values(data).sort((a, b) => {
        const av = a.tokens.reduce(
          (acc, cur) => acc + (cur.dailyTotalVolume || 0n),
          0n
        );
        const bv = b.tokens.reduce(
          (acc, cur) => acc + (cur.dailyTotalVolume || 0n),
          0n
        );

        if (av < bv) {
          return -1;
        }
        if (av > bv) {
          return 1;
        }
        return 0;
      });
    },
    {
      enabled: !!(tokens?.length && vaults?.length),
    }
  );
};

export default useVaultCollections;
